import api from 'api/instance';
import store from 'store/store';

import { Site } from 'types/site';
import { FetchQueryType, FetchResponse } from 'types/common';

const URL = `${process.env.REACT_APP_API_URL}`;

export const fetchSites = async (
  washHub?: string,
  activeOnly?: number,
  archived?: number,
): Promise<Site[]> => {
  const params: any = {
    type: 'site',
    washHub,
    limit: 1000,
  };

  if (activeOnly !== undefined) {
    params.activeOnly = activeOnly;
  }
  if (archived !== undefined) {
    params.archived = archived;
  }
  const { data } = await api.get(`${URL}/location/list`, { params });

  return data?.items;
};

export const fetchPaginatedSites = async (
  params: FetchQueryType & {
    activeOnly?: 0 | 1 | 2;
    archived?: number;
    search?: string;
    sortKey?: string;
    sort?: number;
  },
) => {
  const { activeOnly, archived, search, sortKey, sort, ...restParams } = params;
  const apiParams: any = {
    type: 'site',
    ...restParams,
  };

  // Support activeOnly parameter
  if (activeOnly !== undefined) {
    apiParams.activeOnly = activeOnly;
  }

  // Support archived parameter
  if (archived !== undefined) {
    apiParams.archived = archived;
  }

  // Support search parameter
  if (search) {
    apiParams.search = search;
  }

  // Support sort parameters
  if (sortKey) {
    apiParams.sortKey = sortKey;
  }
  if (sort !== undefined) {
    apiParams.sort = sort;
  } else if (sortKey) {
    // Default sort to 1 if sortKey is provided but sort is not
    apiParams.sort = 1;
  }

  const { data } = await api.get<FetchResponse<Site>>(`${URL}/location/list`, {
    params: apiParams,
  });

  return data;
};

export const fetchSiteById = async (siteId: string): Promise<Site> => {
  const { data } = await api.get(`${URL}/location/detail/${siteId}`);

  return data?.item;
};

export const createSite = async (payload: any) => {
  const { data } = await api.post(`${URL}/location/create/site`, payload);
  return data;
};

export const deleteSites = async (ids: string[]) => {
  const { data } = await api.delete(`${URL}/location/remove-many`, {
    data: { ids },
  });

  return data;
};

export const updateSite = async (payload: any) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(`${URL}/location/site/${_id}`, _payload);

  return data;
};

export const toggleSiteArchive = async (siteId: string) => {
  const { data } = await api.patch(
    `${URL}/location/toggle-site-archive/${siteId}`,
  );

  return data;
};

export const uploadImage = (
  file: File,
  type: string,
  siteId: string,
  isPartnerPortal: boolean,
  onProgress: (progress: number) => void,
) => {
  return new Promise<string>((resolve, reject) => {
    const formData = new FormData();
    formData.append('image', file);

    const state = store.getState();
    const jwtToken = state?.auth?.currentUser?.tokens?.jwtToken;

    const partnerPortalUrl = `${URL}/partner-portal/upload-customer-form-${type}/${siteId}`;
    const siteUrl = `${URL}/location/upload-customer-form-${type}/${siteId}`;

    const url = isPartnerPortal ? partnerPortalUrl : siteUrl;

    const xhr = new XMLHttpRequest();

    xhr.open('POST', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${jwtToken}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const progress = (e.loaded / e.total) * 100;

        onProgress(progress);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 201) {
        const response = JSON.parse(xhr.response);
        const fileName = response?.item?.file;

        resolve(fileName);
      } else {
        reject(
          new Error(
            `Request failed. Status: ${xhr.status}. Content: ${
              JSON.parse(xhr.response)?.message
            }`,
          ),
        );
      }
    };

    xhr.send(formData);
  });
};

export interface ExportCustomerSitesParams {
  washHub?: string;
  washHubName?: string;
  status?: string[];
  search?: string;
  accountManagers?: string[];
  sites?: string[];
}

export const exportCustomerSites = async (
  params: ExportCustomerSitesParams,
): Promise<void> => {
  const { washHubName, ...apiParams } = params;
  const { data } = await api.post(
    `${URL}/location/export-customer-sites`,
    apiParams,
  );

  // API returns CSV string directly
  const csvString = typeof data === 'string' ? data : String(data);

  if (!csvString.trim()) {
    throw new Error('No data returned from API');
  }

  // Find Partner Portal URL column index and update values with full URL
  const lines = csvString.split('\n');
  const headerLine = lines[0];
  const headers = headerLine
    .split(',')
    .map((h) => h.trim().replace(/^"|"$/g, ''));
  const portalUrlIndex = headers.findIndex((h) => h === 'Partner Portal URL');

  let csvContent = csvString;

  // If Partner Portal URL column exists, construct full URLs
  if (portalUrlIndex !== -1) {
    const updatedLines = lines.map((line, lineIndex) => {
      if (lineIndex === 0 || !line.trim()) return line;

      // Parse CSV line properly handling quoted values
      const values: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          values.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current);

      // Update Partner Portal URL with full URL
      if (values[portalUrlIndex] && values[portalUrlIndex] !== '-') {
        const portalName = values[portalUrlIndex].replace(/^"|"$/g, '');
        if (portalName && portalName !== '-') {
          values[
            portalUrlIndex
          ] = `${window.location.origin}/partner/${portalName}`;
        }
      }

      // Rebuild CSV line
      return values
        .map((v) => {
          if (v.includes(',') || v.includes('"') || v.includes('\n')) {
            return `"${v.replace(/"/g, '""')}"`;
          }
          return v;
        })
        .join(',');
    });

    csvContent = updatedLines.join('\n');
  }

  // Create a download link for the CSV file
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const fileName = washHubName
    ? `customer-sites-${washHubName.replace(/\s+/g, '-')}.csv`
    : `customer-sites-${Date.now()}.csv`;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const uploadContract = (
  file: File,
  siteId: string,
  onProgress?: (progress: number) => void,
) => {
  return new Promise<string>((resolve, reject) => {
    const formData = new FormData();
    formData.append('image', file);

    const state = store.getState();
    const jwtToken = state?.auth?.currentUser?.tokens?.jwtToken;

    const url = `${URL}/location/upload-contract/${siteId}`;

    const xhr = new XMLHttpRequest();

    xhr.open('POST', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${jwtToken}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const progress = (e.loaded / e.total) * 100;

        onProgress?.(progress);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 201) {
        const response = JSON.parse(xhr.response);
        const fileName = response?.item?.file;

        resolve(fileName);
      } else {
        reject(
          new Error(
            `Request failed. Status: ${xhr.status}. Content: ${
              JSON.parse(xhr.response)?.message
            }`,
          ),
        );
      }
    };

    xhr.send(formData);
  });
};
